import {
  ContentBlockTypes,
  LandingPageTheme,
  LandingPageThemeUpdatePayload,
  TargetingAttributes,
  TargetingUISettings,
} from 'api/landingPageTheme'
import { ImagePickerValue } from 'components/ImagePicker'
import palette from 'constants/palette'
import { UrlType, UrlValue } from 'scenes/Cms/components/UrlPicker'
import { ContentBlockData } from 'slices/appLandingPageTheme/types'
import { AppLandingPageTheme } from 'slices/appLandingPageTheme/types'

const getDefaultUrlData = (): UrlValue => ({
  inapp: '',
  external: '',
  type: UrlType.External,
  authenticable: false,
})

const getDefaultImageData = (): ImagePickerValue => ({
  id: null,
  url: '',
  uploading: false,
})

const getDefaultTargetingData = (): TargetingUISettings => ({
  audience_type: 'all_customers',
  segment_id: undefined,
  availability_type: 'always_available',
})

// Transform backend format to UI format (reverse of transformTargetingSettings)
const transformBackendTargetingToUI = (
  settings: TargetingAttributes & Record<string, any>,
  blockData?: { start_at?: string; end_at?: string; target_segments?: any[] }
): TargetingUISettings & Record<string, any> => {
  // Start with default targeting data, then merge settings (preserving existing UI fields)
  const defaultTargeting = getDefaultTargetingData()
  const transformedSettings = { ...defaultTargeting, ...settings }

  // Use block-level data if available (top-level fields from content block)
  const startAt = blockData?.start_at || settings.start_at
  const endAt = blockData?.end_at || settings.end_at
  const targetSegments = blockData?.target_segments || settings.target_segments

  // Preserve start_at and end_at fields directly for the UI
  if (startAt) {
    transformedSettings.start_at = startAt
  }

  if (endAt) {
    transformedSettings.end_at = endAt
  }

  // Transform target_segments array to single segment_id for UI
  if (
    targetSegments &&
    Array.isArray(targetSegments) &&
    targetSegments.length > 0
  ) {
    const firstSegment = targetSegments[0]

    // Handle both object format {id: X} and direct number format
    const segmentId =
      typeof firstSegment === 'object' && firstSegment.id
        ? firstSegment.id
        : firstSegment

    transformedSettings.segment_id = segmentId
    transformedSettings.audience_type = 'select_segment'
  } else {
    // If no target segments, default to all customers and clear segment_id
    transformedSettings.audience_type = 'all_customers'
    transformedSettings.segment_id = undefined
  }

  // Infer availability_type from the presence of datetime fields
  if (startAt || endAt) {
    transformedSettings.availability_type = 'set_date_range'
  } else {
    transformedSettings.availability_type = 'always_available'
  }

  return transformedSettings
}

const getDefaultFeaturedContentData = (_: string): ContentBlockData => ({
  type: 'featured_content',
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.white,
    url: '',
    link_target: 'in_app_webview',
  },
  image: getDefaultImageData(),
  url: getDefaultUrlData(),
  ...getDefaultTargetingData(),
})

const getDefaultStoredValueBalanceData = (_: string): ContentBlockData => ({
  type: 'stored_value_balance',
  settings: {
    padding_top: 12,
    padding_right: 12,
    padding_bottom: 12,
    padding_left: 12,
    border_radius: 2,
    border_width: 1,
    border_color: palette.grey30,
    background_color: palette.grey10,
    header_text_color: palette.grey90,
    body_text_color: palette.grey70,
    chevron_color: palette.grey90,
  },
  ...getDefaultTargetingData(),
})

const getDefaultMissingLoyaltyPromptData = (
  primaryColor: string
): ContentBlockData => ({
  type: 'missing_loyalty_prompt',
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.white,
    background_color: palette.grey10,
    header_text_color: palette.grey90,
    body_text_color: palette.grey70,
    button_border_color: primaryColor,
    button_text_color: primaryColor,
    close_icon_color: palette.black,
  },
  ...getDefaultTargetingData(),
})

const getDefaultRewardsData = (primaryColor: string): ContentBlockData => ({
  type: 'rewards',
  settings: {
    padding_top: 14,
    padding_right: 12,
    padding_bottom: 14,
    padding_left: 12,
    background_color: palette.white,
    border_color: palette.grey30,
    border_width: 1,
    border_radius: 1,
    header_text_color: palette.grey90,
    body_text_color: palette.grey90,
    item_spacing: 8,
    button_border_color: primaryColor,
    button_text_color: primaryColor,
    link_color: primaryColor,
  },
  ...getDefaultTargetingData(),
})

const getDefaultRecentOrdersData = (
  primaryColor: string
): ContentBlockData => ({
  type: 'recent_orders',
  settings: {
    padding_top: 12,
    padding_right: 12,
    padding_bottom: 0,
    padding_left: 12,
    border_radius: 2,
    border_width: 1,
    border_color: palette.grey30,
    background_color: '#FFFFFF',
    header_text_color: palette.grey90,
    body_text_color: palette.grey50,
    item_spacing: 12,
    button_bg_color: primaryColor,
    button_text_color: palette.white,
  },
  ...getDefaultTargetingData(),
})

const getDefaultMenuCategoriesData = (_: string): ContentBlockData => ({
  type: 'menu_categories',
  settings: {
    padding_top: 12,
    padding_right: 12,
    padding_bottom: 0,
    padding_left: 12,
    border_radius: 2,
    border_width: 1,
    border_color: palette.grey30,
    background_color: palette.white,
    header_text_color: palette.grey90,
    item_spacing: 12,
  },
  ...getDefaultTargetingData(),
})

const getDefaultExternalFormData = (_: string): ContentBlockData => ({
  type: 'external_form',
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.white,
    form_id: undefined,
  },
  image: getDefaultImageData(),
  ...getDefaultTargetingData(),
})

const getDefaultPersonalizedTextData = (
  appLandingPageSettings: AppLandingPageTheme['settings']
): ContentBlockData => ({
  type: 'personalized_text',
  url: getDefaultUrlData(),
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.white,
    background_color: appLandingPageSettings.background_color || palette.grey10,
    text_color: appLandingPageSettings.header_text_color || palette.grey90,
    text: '',
    text_align: 'center',
    fallback_behavior: 'hide',
    link_target: 'in_app_webview',
    placeholder_values: {
      first_name: 'User',
      last_name: 'User',
    },
  },
  ...getDefaultTargetingData(),
})

const getDefaultFeaturedContentCarouselData = (
  _: string
): ContentBlockData => ({
  type: 'featured_content_carousel',
  featured_contents: {},
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.grey30,
    background_color: palette.white,
    header_text_color: palette.grey90,
    body_text_color: palette.grey70,
    item_spacing: 0,
  },
  ...getDefaultTargetingData(),
})

const getDefaultGameData = (_primaryColor: string): ContentBlockData => ({
  type: 'game',
  image: getDefaultImageData(),
  settings: {
    padding_top: 0,
    padding_right: 0,
    padding_bottom: 0,
    padding_left: 0,
    border_radius: 0,
    border_width: 0,
    border_color: palette.white,
    brame_campaign_id: undefined,
  },
  ...getDefaultTargetingData(),
})

export const getDefaultBlockData = (
  type: ContentBlockTypes,
  primaryColor: string,
  appLandingPageSettings: AppLandingPageTheme['settings']
): ContentBlockData | null => {
  switch (type) {
    case 'featured_content_carousel':
      return getDefaultFeaturedContentCarouselData(primaryColor)
    case 'featured_content':
      return getDefaultFeaturedContentData(primaryColor)
    case 'stored_value_balance':
      return getDefaultStoredValueBalanceData(primaryColor)
    case 'missing_loyalty_prompt':
      return getDefaultMissingLoyaltyPromptData(primaryColor)
    case 'rewards':
      return getDefaultRewardsData(primaryColor)
    case 'recent_orders':
      return getDefaultRecentOrdersData(primaryColor)
    case 'menu_categories':
      return getDefaultMenuCategoriesData(primaryColor)
    case 'external_form':
      return getDefaultExternalFormData(primaryColor)
    case 'personalized_text':
      return getDefaultPersonalizedTextData(appLandingPageSettings)
    case 'game':
      return getDefaultGameData(primaryColor)
    default:
      return null
  }
}

const BUTTON_STYLE_KEYS = [
  'button_text_color',
  'button_bg_color',
  'button_border_color',
] as const

// Existing carousel blocks can load without button-color keys (never saved, or stripped
// server-side), leaving the Style editor's button pickers hidden. Backfill the type's
// default button colors for any the settings lack so the pickers render; persisted wins.
export const withButtonColorDefaults = (
  type: ContentBlockTypes,
  settings: Record<string, any>,
  primaryColor: string,
  appLandingPageSettings: AppLandingPageTheme['settings']
): Record<string, any> => {
  const defaults: Record<string, any> =
    getDefaultBlockData(type, primaryColor, appLandingPageSettings)?.settings ??
    {}

  const backfilled = { ...settings }
  BUTTON_STYLE_KEYS.forEach(key => {
    if (key in defaults && !(key in backfilled)) {
      backfilled[key] = defaults[key]
    }
  })
  return backfilled
}

export const hasImage = (type: ContentBlockTypes): boolean => {
  switch (type) {
    case 'featured_content':
    case 'external_form':
    case 'game':
      return true
    default:
      return false
  }
}

type GetSubmitPayloadT = {
  blockOrder: Array<string>
  formModel: AppLandingPageTheme
  landingPageTheme: LandingPageTheme | null
}

const getUrlAddress = (contentBlock: any): string | undefined => {
  if (!('url' in contentBlock)) return undefined

  const url = contentBlock.url
  if (url?.type === UrlType.NoLink) {
    return ''
  } else {
    let urlAddress = url?.[url.type]
    if (
      url?.type === UrlType.Inapp &&
      url.inapp.startsWith('thanx://ordering-menu') &&
      url.queryParams?.split('category=')[1]
    ) {
      urlAddress += `?${url.queryParams}`
    }
    return urlAddress
  }
}

const getFileUploads = (contentBlock: any): any => {
  const fileUploads: any = {}

  // Handle direct image uploads on the content block
  if ('image' in contentBlock && contentBlock.image?.id) {
    fileUploads.image = {
      file_upload_id: contentBlock.image.id,
    }
  }

  // Handle image uploads from carousel featured contents
  if ('featured_contents' in contentBlock && contentBlock.featured_contents) {
    Object.entries(contentBlock.featured_contents).forEach(
      ([slideId, slideData]: [string, any]) => {
        if (
          slideData &&
          typeof slideData === 'object' &&
          'image' in slideData &&
          slideData.image?.id
        ) {
          fileUploads[`featured_content_${slideId}_image`] = {
            file_upload_id: slideData.image.id,
          }
        }
      }
    )
  }

  return Object.keys(fileUploads).length > 0 ? fileUploads : undefined
}

// Helper function to handle common targeting transformation for all content block types
const transformTopLevelTargetingFields = (contentBlock: any) => {
  // Extract targeting fields directly from contentBlock
  const targetingData = {
    audience_type: contentBlock.audience_type,
    segment_id: contentBlock.segment_id,
    availability_type: contentBlock.availability_type,
    start_at: contentBlock.start_at,
    end_at: contentBlock.end_at,
  }

  // If no targeting data exists, return empty
  if (isEmptyTargetingData(targetingData)) {
    return {}
  }

  let transformedTargeting = transformTemporalSettings(targetingData)
  transformedTargeting = transformSegmentSettings(transformedTargeting)

  // Extract only the backend targeting fields, not UI fields
  const { start_at, end_at, target_segments } = transformedTargeting

  return {
    ...(start_at !== undefined && { start_at }),
    ...(end_at !== undefined && { end_at }),
    ...(target_segments && { target_segments }),
  }
}

const transformAndExtractTargetingFields = (
  contentBlock: any,
  settings: any
) => {
  const targetingFields = transformTopLevelTargetingFields(contentBlock)

  return {
    targetingFields,
    cleanSettings: settings,
  }
}

const getFeaturedContentSubmitPayload = (
  contentBlock: any,
  block: any,
  index: number
) => {
  const { type, settings } = contentBlock

  const file_uploads = getFileUploads(contentBlock)
  const urlAddress = getUrlAddress(contentBlock)

  const combinedSettings = {
    ...settings,
    ...(typeof urlAddress === 'string' ? { url: urlAddress } : {}),
  }

  const { targetingFields, cleanSettings } = transformAndExtractTargetingFields(
    contentBlock,
    combinedSettings
  )

  return {
    id: block?.id,
    position: index + 1,
    type,
    ...targetingFields,
    settings: cleanSettings,
    file_uploads,
  }
}

const getExternalFormSubmitPayload = (
  contentBlock: any,
  block: any,
  index: number
) => {
  const { type, settings } = contentBlock

  const file_uploads = getFileUploads(contentBlock)

  const { targetingFields, cleanSettings } = transformAndExtractTargetingFields(
    contentBlock,
    settings
  )

  return {
    id: block?.id,
    position: index + 1,
    type,
    ...targetingFields,
    settings: cleanSettings,
    file_uploads,
  }
}

const getGameSubmitPayload = (contentBlock: any, block: any, index: number) => {
  const { type, settings } = contentBlock

  const file_uploads = getFileUploads(contentBlock)

  const { targetingFields, cleanSettings } = transformAndExtractTargetingFields(
    contentBlock,
    settings
  )

  return {
    id: block?.id,
    position: index + 1,
    type,
    ...targetingFields,
    settings: cleanSettings,
    file_uploads,
  }
}

const getFeaturedContentCarouselSubmitPayload = (
  contentBlock: any,
  block: any,
  index: number
) => {
  const { type, settings } = contentBlock

  const { targetingFields, cleanSettings } = transformAndExtractTargetingFields(
    contentBlock,
    settings
  )

  // Needed to ensure the featured_contents don't get sent as nested content blocks,
  // as the back-end doesn't currently support nested content blocks
  return {
    id: block?.id,
    position: index + 1,
    type,
    ...targetingFields,
    settings: cleanSettings,
  }
}

// Note: when the carousel id is missing on the back-end, thus the slide id is also null (new slide)
//       the back-end will automatically attach all slides to the same carousel
function getCarouselFeaturedContentBlock(
  slideData: any,
  existingSlide: any,
  sourceId: number
) {
  const { type, settings } = slideData

  const file_uploads = getFileUploads(slideData)
  const urlAddress = getUrlAddress(slideData)

  let updatedSettings = {
    ...settings,
    ...(typeof urlAddress === 'string' ? { url: urlAddress } : {}),
  }

  // Transform targeting settings for backend
  updatedSettings = transformTargetingSettings(updatedSettings)

  const payload: any = {
    // Handle ID for new vs existing slides; new slide have nil ids
    id: existingSlide?.id,
    position: slideData?.position || 1,
    type: type,
    settings: updatedSettings,
    file_uploads,
    source_id: sourceId,
    source_type: 'featured_content_carousel',
  }

  return payload
}

const isEmptyTargetingData = (targetingData: TargetingUISettings): boolean => {
  return (
    !targetingData ||
    (targetingData.audience_type === undefined &&
      targetingData.segment_id === undefined &&
      targetingData.availability_type === undefined)
  )
}

// Transform temporal (datetime) settings from UI format to backend format
const transformTemporalSettings = (
  settings: TargetingUISettings & Record<string, any>
): TargetingAttributes & Record<string, any> => {
  const transformedSettings = { ...settings }

  // Handle availability clearing: if "always_available", clear all temporal data
  const isAlwaysAvailable = settings.availability_type === 'always_available'

  if (isAlwaysAvailable) {
    // Clear datetime fields when always available
    transformedSettings.start_at = null
    transformedSettings.end_at = null
  }
  // Otherwise, pass through start_at and end_at directly since UI now uses combined datetime fields

  return transformedSettings
}

// Transform segment settings from UI format to backend format
const transformSegmentSettings = (
  settings: TargetingUISettings & Record<string, any>
): TargetingAttributes & Record<string, any> => {
  const transformedSettings = { ...settings }

  // Handle audience clearing: if "all_customers", always clear segment data
  const isAllCustomers = settings.audience_type === 'all_customers'

  // Transform segment_id to target_segments array for backend
  if (!isAllCustomers && settings.segment_id) {
    transformedSettings.target_segments = [settings.segment_id]
  } else {
    // Clear target_segments when "all_customers" is selected or no segment is selected
    transformedSettings.target_segments = []
  }

  // Always remove UI field from backend payload
  delete transformedSettings.segment_id

  return transformedSettings
}

// Main function that orchestrates all targeting settings transformations
const transformTargetingSettings = (
  settings: TargetingUISettings & Record<string, any>
): TargetingAttributes & Record<string, any> => {
  // If settings is empty or doesn't have any targeting data, return as-is to avoid overriding
  if (isEmptyTargetingData(settings)) {
    return { ...settings }
  }

  let transformedSettings = { ...settings }
  transformedSettings = transformTemporalSettings(transformedSettings)
  transformedSettings = transformSegmentSettings(transformedSettings)

  // Keep audience_type and availability_type for backend storage
  return transformedSettings
}

export { transformBackendTargetingToUI, transformTargetingSettings }

const getGenericSubmitPayload = (
  contentBlock: any,
  block: any,
  index: number
) => {
  const { type, settings } = contentBlock

  const urlAddress = getUrlAddress(contentBlock)
  const combinedSettings = {
    ...settings,
    ...(typeof urlAddress === 'string' ? { url: urlAddress } : {}),
  }

  const { targetingFields, cleanSettings } = transformAndExtractTargetingFields(
    contentBlock,
    combinedSettings
  )

  return {
    id: block?.id,
    position: index + 1,
    type,
    ...targetingFields,
    settings: cleanSettings,
  }
}

function getDeletedSlideBlocks(
  blockOrder: string[],
  formModel: any,
  landingPageTheme: any
) {
  const deleteSlideBlocks: any[] = []

  blockOrder.forEach(blockId => {
    const contentBlock = formModel.contentBlocks[blockId]
    const block = (landingPageTheme?.content_blocks ?? []).find(
      b => `${b.id}` === blockId
    )

    if (
      contentBlock.type === 'featured_content_carousel' &&
      block?.featured_contents
    ) {
      // Get current slide IDs from form state - these are the actual backend IDs that still exist
      const formSlideIds = Object.values(contentBlock.featured_contents || {})
        .map((slideData: any) => slideData?.id)
        .filter(id => id && !id.toString().startsWith('slide-')) // Only existing backend IDs (not new slides)

      const backendSlides = block.featured_contents

      backendSlides.forEach((slide: any) => {
        if (slide.id && !formSlideIds.includes(slide.id)) {
          deleteSlideBlocks.push({
            id: slide.id,
            destroy: true,
            // When destroying carousel slides, we need to also specify the source.
            source_id: block.id,
            source_type: 'featured_content_carousel',
          })
        }
      })
    }
  })

  return deleteSlideBlocks
}

export function getSubmitPayload({
  blockOrder,
  formModel,
  landingPageTheme,
  featuredContentCarouselEnabled = false,
}: GetSubmitPayloadT & {
  featuredContentCarouselEnabled?: boolean
}): LandingPageThemeUpdatePayload['landing_page_theme'] {
  const blockUpdates: LandingPageThemeUpdatePayload['landing_page_theme']['content_blocks'] =
    []

  blockOrder.forEach((blockId, index) => {
    const contentBlock = formModel.contentBlocks[blockId]
    const { type } = contentBlock
    const block = (landingPageTheme?.content_blocks ?? []).find(
      b => `${b.id}` === blockId
    )

    let blockData: any

    switch (type) {
      case 'featured_content':
        blockData = getFeaturedContentSubmitPayload(contentBlock, block, index)
        break
      case 'external_form':
        blockData = getExternalFormSubmitPayload(contentBlock, block, index)
        break
      case 'game':
        blockData = getGameSubmitPayload(contentBlock, block, index)
        break
      case 'featured_content_carousel':
        blockData = getFeaturedContentCarouselSubmitPayload(
          contentBlock,
          block,
          index
        )
        break
      default:
        blockData = getGenericSubmitPayload(contentBlock, block, index)
        break
    }

    blockUpdates.push(blockData)

    // If feature flag is enabled, include flattened featured content blocks with the API requests
    // Note: the carousel featured_contents need to be added after the carousel
    if (
      featuredContentCarouselEnabled &&
      type === 'featured_content_carousel'
    ) {
      if (contentBlock.featured_contents) {
        blockUpdates.push(
          ...getFeaturedContentBlocksFromCarousels(
            blockOrder,
            formModel,
            landingPageTheme
          )
        )
      }
    }
  })

  const deleteContentBlocks = (landingPageTheme?.content_blocks ?? [])
    .filter(({ id }) => blockOrder.indexOf(`${id}`) === -1)
    .map(({ id }) => ({
      id,
      destroy: true,
    }))

  // Get deleted slides if feature flag is enabled
  const deleteSlideBlocks = featuredContentCarouselEnabled
    ? getDeletedSlideBlocks(blockOrder, formModel, landingPageTheme)
    : []

  return {
    id: landingPageTheme?.id,
    content_blocks: blockUpdates
      .concat(deleteContentBlocks)
      .concat(deleteSlideBlocks),
    settings: formModel.settings,
  }
}

export function getFeaturedContentBlocksFromCarousels(
  blockOrder: string[],
  formModel: any,
  landingPageTheme: any
) {
  const allContentBlocks: any[] = []

  blockOrder.forEach(blockId => {
    const contentBlock = formModel.contentBlocks[blockId]
    const block = (landingPageTheme?.content_blocks ?? []).find(
      b => `${b.id}` === blockId
    )

    if (
      contentBlock.type === 'featured_content_carousel' &&
      contentBlock.featured_contents &&
      Object.keys(contentBlock.featured_contents).length > 0
    ) {
      // Loop over featured contents and create content blocks
      Object.entries(contentBlock.featured_contents).forEach(
        ([_, slideData]: [string, any]) => {
          const existingSlide = (block?.featured_contents ?? []).find(
            (slide: any) => slide.id?.toString() === slideData.id?.toString()
          )

          const contentBlock = getCarouselFeaturedContentBlock(
            slideData,
            existingSlide,
            block?.id
          )
          allContentBlocks.push(contentBlock)
        }
      )
    }
  })

  return allContentBlocks
}

// Slug matches breact slug parser
